import { AIButton, useAIAssistant } from '@cobliteam/ai';
import { Analytics } from '@cobliteam/analytics';
import type { SessionUser } from '@cobliteam/common-types';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import GlobalSearch from '@cobliteam/global-search';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import Button from '@cobliteam/ui/components/Button';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import AppUserButton from '@cobliteam/ui-business/components/AppUser';
import Feedback from '@cobliteam/ui-business/components/Feedback';
import { memo } from 'react';
import { SidebarTrigger } from '../AppSidebar';
import FilterGroupComponent from './components/FilterGroupComponent';
import LogoComponent from './components/LogoComponent';
import NotificationsButton from './components/Notifications/NotificationsButton';

interface AppHeaderProps {
  user: SessionUser;
  logout: () => void;
}

const AppHeader = ({ user, logout }: AppHeaderProps) => {
  const { runtimeFlags, sidebar } = useDashboard();
  const featureFlags = useFeatureFlags();
  const { isAISidepanelOpen, closeAISidepanel, openAISidepanel } = useAIAssistant();
  const isAIRouter = sidebar.route?.id === 'ai';

  return (
    <header
      data-testid='app-header'
      className={cn('sticky top-0 z-50 flex h-14 items-center gap-2 border-gray-6 border-b bg-white px-2 pl-4 shadow-sm transition-shadow duration-200')}
    >
      <div className='relative z-10 flex min-w-0 shrink-0 items-center justify-start gap-2'>
        <SidebarTrigger className='-ml-1 mr-2 desktop:hidden rounded-lg border border-gray-5' />
        <LogoComponent />
        <FilterGroupComponent />
      </div>

      <div className='pointer-events-none absolute inset-0 z-0 flex items-center justify-center px-4'>
        <div className='pointer-events-auto max-w-full'>{!runtimeFlags.noValidGroupOrVehicles && <GlobalSearch />}</div>
      </div>

      <div className='relative z-10 ml-auto flex shrink-0 items-center justify-end gap-2.5'>
        {featureFlags.aiChat && (
          <AIButton
            onClick={() => {
              isAISidepanelOpen ? closeAISidepanel() : openAISidepanel();
            }}
            showTooltip
            isSelected={isAISidepanelOpen}
            dataTestId='app-header-ai-button'
            size='sm'
            disabled={isAIRouter}
            variant={isAISidepanelOpen ? 'solid' : 'soft'}
          />
        )}
        <Feedback source='navbar'>
          <Button
            data-testid='app-header-feedback-button'
            variant='outlined'
            color='default'
            size='sm'
            icon={<Icon name='megaphone' />}
            className='group'
            onClick={() => Analytics.track('NavBar - Feedback - Click')}
          />
        </Feedback>
        <NotificationsButton />
        <AppUserButton user={user} logout={logout} />
      </div>
    </header>
  );
};

export default memo(AppHeader, (prevProps, nextProps) => {
  return prevProps.user.id === nextProps.user.id;
});
